/* Skarpenord: a modern, digital proposal letter with a product inside. Big type, calm surfaces, very few elements. */
import { useEffect, useRef, useState } from "react";
import { Check, Download } from "lucide-react";
import { KvitebergLogo } from "../KvitebergLogo";
import { fmtDate, monthLabel, useKeys, useMeta, useReveal } from "./proposalUtils";
import { downloadProposalPdf } from "../../pdf/download";
import { HeroShot, SystemShots, WorkspaceShot } from "./sk/Shots";
import { LiveSystem } from "./LiveSystem";
import { useCountUp } from "./motion";
import { nok } from "../../data/tilbod/priceModel";
import { Demo } from "./Demo";
import { AiExplorer } from "./AiExplorer";

/* >= 700 px: the real system is previewed inline under the letter. Below that, one button opens it full screen. */
const useWide = () => {
  const [wide, setWide] = useState(() => (typeof window !== "undefined" ? window.matchMedia("(min-width: 700px)").matches : true));
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 700px)");
    const onChange = (e) => setWide(e.matches);
    setWide(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return wide;
};

const usePdf = (d) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await downloadProposalPdf(d);
    } catch (e) {
      console.error("PDF", e);
      window.print();
    } finally {
      setBusy(false);
    }
  };
  return [busy, run];
};

/* how far down the letter the reader is (0–1) – drawn as a hairline under the top bar */
const useProgress = () => {
  const [p, setP] = useState(0);
  useEffect(() => {
    let raf = 0;
    const measure = () => {
      raf = 0;
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      setP(max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0);
    };
    const onScroll = () => {
      if (!raf) raf = window.requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, []);
  return p;
};

/* the PDF button: an icon, the label, a spinner while the file is made */
const PdfButton = ({ d, label, className = "", testId }) => {
  const [busy, onPdf] = usePdf(d);
  return (
    <button type="button" className={`kvl-btn ${className}`} onClick={onPdf} disabled={busy} aria-busy={busy} data-testid={testId}>
      {busy ? <span className="kvl-spin" aria-hidden="true" /> : <Download className="kvl-btn-ic" aria-hidden="true" />}
      <span>{busy ? d.ui.pdfBusy : label}</span>
    </button>
  );
};

/* top bar: the logo, the PDF – and a hairline that shows how far the reader has come */
const Top = ({ d }) => {
  const p = useProgress();
  const wide = useWide();
  const lt = d.langToggle;
  return (
    <div className={`kvl-top ${p > 0.005 ? "is-scrolled" : ""}`} data-testid="tilbod-topbar">
      <div className="kvl-wide kvl-top-in">
        <a href="#cover" className="kvl-logo" aria-label="Kviteberg">
          <KvitebergLogo className="kvl-logo-svg" />
        </a>
        <div className="kvl-top-actions">
          {lt && (
            <div className="kvl-lang" role="group" aria-label="Language" data-testid="tilbod-lang-toggle">
              {lt.options.map(([code, label]) => (
                <button key={code} type="button" lang={code === "no" ? "nb" : code} className={code === lt.current ? "is-on" : ""} aria-pressed={code === lt.current} aria-label={label} onClick={() => lt.set(code)} data-testid={`tilbod-lang-${code}`}>
                  {code.toUpperCase()}
                </button>
              ))}
            </div>
          )}
          <PdfButton d={d} label={wide ? d.ui.pdf : d.ui.pdfShort || d.ui.pdf} className="kvl-btn-sm" testId="tilbod-print-btn" />
        </div>
      </div>
      <span className="kvl-top-progress" style={{ transform: `scaleX(${p})` }} aria-hidden="true" data-testid="tilbod-progress" />
    </div>
  );
};

const Lines = ({ text, as: Tag = "h2", className, testid }) => (
  <Tag className={className} data-testid={testid}>
    {text.split("\n").map((l, i) => (
      <span key={l} className="kvl-line">
        {l}
        {i < text.split("\n").length - 1 && <br />}
      </span>
    ))}
  </Tag>
);

/* a section = one big heading + a reading column; optional wide figure below (or right after the heading). tone = paper | white | sand | dark */
const Sec = ({ id, head, eyebrow, title, children, figure, figureFirst, testid, tone = "paper", className = "" }) => {
  const ref = useReveal();
  return (
    <section id={id} className={`kvl-sec kvl-tone-${tone} ${className}`} data-head={head || (title ? title.split("\n")[0] : "")} data-tone={tone} data-testid={testid} ref={ref}>
      <div className="kvl-col kvl-reveal">
        {eyebrow && (
          <p className="kvl-sec-eyebrow" data-testid={`${testid}-eyebrow`}>
            {eyebrow}
          </p>
        )}
        {title && <Lines text={title} className="kvl-h2" testid={`${testid}-title`} />}
        {!figureFirst && children}
      </div>
      {figure && <div className="kvl-reveal kvl-reveal-2">{figure}</div>}
      {figureFirst && <div className="kvl-col kvl-reveal kvl-reveal-2 kvl-after">{children}</div>}
    </section>
  );
};

const Paras = ({ items, className = "kvl-p" }) =>
  (Array.isArray(items) ? items : [items]).filter(Boolean).map((x) => (
    <p key={x} className={className}>
      {x}
    </p>
  ));

/* a prototype moment: wide, minimal frame, one caption */
const Figure = ({ children, caption, testid, className = "", style }) => (
  <figure className={`kvl-figure ${className}`} style={style} data-testid={testid}>
    <div className="kvl-frame">{children}</div>
    <figcaption>{caption}</figcaption>
  </figure>
);

/* the hero: a cover. Kicker, the headline in two sizes and one paragraph, centred; the document's facts
   (to · from · reference · valid until) as one hairlined strip; the system itself wide underneath. */
const Hero = ({ d, date, valid }) => {
  const month = monthLabel(d, date);
  const h = d.hero;
  const kicker = `${d.ui.proposalTo} ${d.to.company}, ${d.lang === "en" ? month : month.toLowerCase()} ${date.getFullYear()}`;
  const meta = [
    [d.ui.to, d.to.person, [d.to.role, d.to.company].filter(Boolean).join(", ")],
    [d.ui.from, d.from.person, d.from.company],
    [d.ui.reference, d.ref, null],
    [d.ui.validUntil, fmtDate(valid, d.lang), null],
  ];
  return (
    <header id="cover" className="kvl-hero kvl-hero-v6" data-head={d.ui.proposal} data-testid="tilbod-cover">
      <div className="kvl-hero6-head">
        <p className="kvl-hero-kicker kvl-rise" data-testid="tilbod-eyebrow">
          {kicker}
        </p>
        <Lines text={d.title} as="h1" className="kvl-h1 kvl-rise" testid="tilbod-title" />
        <p className="kvl-hero-sub kvl-rise" style={{ "--d": "0.1s" }} data-testid="tilbod-subtitle">
          {h.sub}
        </p>
        <dl className="kvl-hero6-meta kvl-rise" style={{ "--d": "0.18s" }} data-testid="tilbod-hero-meta">
          {meta.map(([k, v, sub]) => (
            <div key={k}>
              <dt>{k}</dt>
              <dd>
                {v}
                {sub && <span>{sub}</span>}
              </dd>
            </div>
          ))}
        </dl>
      </div>
      <div className="kvl-hero-stage kvl-rise" style={{ "--d": "0.26s" }} data-testid="tilbod-hero-shot">
        <div className="kvl-frame kvl-hero-frame">
          <HeroShot d={d} />
        </div>
      </div>
    </header>
  );
};

/* the letter: a greeting, the paragraphs, and the person who signs it */
const Letter = ({ d }) => {
  const ref = useReveal();
  return (
    <section id="letter" className="kvl-sec kvl-tone-white kvl-letter-sec" data-head={d.letter.greeting.replace(/,$/, "")} data-tone="white" data-testid="tilbod-letter" ref={ref}>
      <div className="kvl-col kvl-reveal">
        <p className="kvl-greeting" data-testid="tilbod-greeting">
          {d.letter.greeting}
        </p>
        <Paras items={d.letter.p} />
        <div className="kvl-sign" data-testid="tilbod-sign">
          <p className="kvl-p kvl-sign-close">{d.letter.close}</p>
          <div className="kvl-signer">
            <img src={d.from.photo} alt="" className="kvl-signer-img" width="72" height="72" loading="lazy" data-testid="tilbod-signer-photo" />
            <div>
              <span className="kvl-signer-name">{d.from.person}</span>
              <span className="kvl-signer-role">
                {d.from.role} · {d.from.company}
              </span>
              <a className="kvl-signer-mail" href={`mailto:${d.from.email}`} data-testid="tilbod-signer-mail">
                {d.from.email}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* the seven steps: a ledger – number, title, text, and what the step leaves behind – one hairline between each row */
const Flow = ({ f }) => (
  <ol className="kvl-wide kvl-flow" data-testid="tilbod-flow-steps">
    {f.steps.map(([k, v, out], i) => (
      <li key={k} className="kvl-flow-row" style={{ "--i": i }} data-testid={`tilbod-flow-${i + 1}`}>
        <span className="kvl-flow-n" aria-hidden="true">
          {String(i + 1).padStart(2, "0")}
        </span>
        <h3 className="kvl-flow-k">{k}</h3>
        <p className="kvl-flow-p">{v}</p>
        {out && (
          <span className="kvl-flow-out" data-testid={`tilbod-flow-out-${i + 1}`}>
            <span className="kvl-flow-out-l">{f.outLabel}</span>
            <span className="kvl-flow-out-v">{out}</span>
          </span>
        )}
      </li>
    ))}
  </ol>
);

const Roles = ({ r }) => (
  <div className="kvl-wide kvl-roles-wrap" data-testid="tilbod-ai-roles">
    <h3 className="kvl-roles-h">{r.title}</h3>
    <ol className="kvl-roles">
      {r.items.map(([who, does, p], i) => (
        <li key={does} className={`kvl-role kvl-role-${i + 1}`} style={{ "--i": i }} data-testid={`tilbod-ai-role-${i + 1}`}>
          {who && <span className="kvl-role-who">{who}</span>}
          <span className="kvl-role-does">{does}</span>
          <p className="kvl-role-p">{p}</p>
        </li>
      ))}
    </ol>
  </div>
);

/* «54 ventiler → aktuatorforslag per ventil → 6 punkter til teknisk vurdering» */
const Example = ({ ex }) => {
  const parts = ex.parts || [ex.from, ex.to];
  return (
    <div className="kvl-col kvl-example-wrap">
      {ex.label && <span className="kvl-sec-eyebrow kvl-example-label">{ex.label}</span>}
      <p className="kvl-example" data-testid="tilbod-ai-example">
        {parts.map((x, i) => (
          <span key={x} className="kvl-example-part">
            {i > 0 && (
              <span className="kvl-arrow" aria-hidden="true">
                →
              </span>
            )}
            <span>{x}</span>
          </span>
        ))}
      </p>
    </div>
  );
};

/* KI: a centred head, then the explorer (six tasks · one card), the three roles, and the system itself */
const AiSection = ({ d }) => {
  const ai = d.ai;
  const ref = useReveal();
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.12 });
    io.observe(el);
    return () => io.disconnect();
  }, [ref]);
  return (
    <section id="ai" className="kvl-sec kvl-tone-sand kvl-ai-sec kvl-sec-center" data-head={ai.title.split(" ")[0]} data-tone="sand" data-testid="tilbod-ai" ref={ref}>
      <div className="kvl-col kvl-reveal">
        {ai.eyebrow && <p className="kvl-sec-eyebrow">{ai.eyebrow}</p>}
        <Lines text={ai.title} className="kvl-h2" testid="tilbod-ai-title" />
        <Paras items={ai.p} />
      </div>
      <div className="kvl-reveal kvl-reveal-2">
        <AiExplorer ai={ai} inView={inView} />
      </div>
      {ai.roles && (
        <div className="kvl-reveal kvl-reveal-2">
          <Roles r={ai.roles} />
        </div>
      )}
      <div className="kvl-reveal kvl-reveal-2">
        <Example ex={ai.example} />
        <Figure caption={ai.example.caption} testid="tilbod-ai-figure" className="kvl-figure-mock">
          <WorkspaceShot d={d} />
        </Figure>
      </div>
    </section>
  );
};

/* Dynamics: the two systems side by side, what moves between them in the middle, what is included and what comes later */
const Exchange = ({ v }) => (
  <div className="kvl-wide kvl-x-wrap">
    <div className="kvl-x" data-testid="tilbod-visma-chain">
      {[0, 1].map((i) => (
        <div key={v.line[i]} className={`kvl-x-sys kvl-x-sys-${i + 1}`} style={{ gridArea: `s${i + 1}` }} data-testid={`tilbod-visma-node-${i + 1}`}>
          {v.tags && <span className="kvl-x-tag">{v.tags[i]}</span>}
          <span className="kvl-x-title">{v.line[i]}</span>
          <span className="kvl-x-sub">{v.under[i]}</span>
        </div>
      ))}
      <ol className="kvl-x-flows" style={{ gridArea: "f" }} data-testid="tilbod-visma-flows">
        {v.exchange.map((f, i) => (
          <li key={f.title} className={`kvl-x-flow is-${f.dir}`} style={{ "--i": i }} data-testid={`tilbod-visma-flow-${i + 1}`}>
            <span className="kvl-x-what">{f.title}</span>
            <span className="kvl-x-line" aria-hidden="true" />
            <span className="kvl-x-when">{f.p}</span>
          </li>
        ))}
      </ol>
    </div>
    <div className="kvl-x-foot">
      {v.scope && (
        <div className="kvl-x-scope" data-testid="tilbod-visma-phase">
          <h3 className="kvl-x-h">{v.scope.title}</h3>
          <Paras items={v.scope.p} className="kvl-x-phase" />
        </div>
      )}
      {v.later && (
        <div className="kvl-x-later" data-testid="tilbod-visma-later">
          <span className="kvl-x-tag">{v.later.tag}</span>
          <p>{v.later.text}</p>
        </div>
      )}
    </div>
    {v.note && (
      <p className="kvl-small kvl-x-note" data-testid="tilbod-visma-note">
        {v.note}
      </p>
    )}
  </div>
);

/* where it runs: three ways, the assumed one first – and one line on access and data */
const Platform = ({ pf }) => (
  <div className="kvl-wide kvl-opts-wrap">
    <ol className="kvl-opts" data-testid="tilbod-platform-options">
      {pf.options.map((o, i) => (
        <li key={o.name} className={o.primary ? "is-primary" : ""} style={{ "--i": i }} data-testid={`tilbod-platform-option-${i + 1}`}>
          <span className="kvl-opts-tag">{o.tag}</span>
          <h3 className="kvl-opts-name">{o.name}</h3>
          <p className="kvl-opts-p">{o.p}</p>
        </li>
      ))}
    </ol>
    {pf.note && (
      <div className="kvl-opts-note" data-testid="tilbod-platform-note">
        {pf.noteTitle && <h3 className="kvl-x-h">{pf.noteTitle}</h3>}
        <p className="kvl-p">{pf.note}</p>
      </div>
    )}
  </div>
);

/* the price: one light sheet. Two deliveries side by side with equal weight – the customer portal (firm) and the internal
   operations system (estimate) – each with its establishment and licence; then the estimate as one small table
   (per delivery and both together: first year, every year after), our advice, and the payment plan. Details live behind
   «Se leveranseomfanget». Every amount comes from price.model / price.estimate – nothing is typed twice. */
const Tick = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" className="kvl-tick">
    <path d="M3 8.5 6.4 12 13 4.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* true once the element has been on screen (immediately when motion is reduced) */
const useSeen = () => {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === "undefined" || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setSeen(true);
      return undefined;
    }
    const io = new IntersectionObserver((es) => {
      if (es.some((e) => e.isIntersecting)) {
        setSeen(true);
        io.disconnect();
      }
    }, { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, seen];
};

const PriceModule = ({ m, pr, i }) => (
  <article className="kvl-price-mod" data-kind={m.estimate ? "estimate" : "offer"} style={{ "--i": i }} data-testid={`tilbod-price-mod-${m.key}`}>
    <header className="kvl-price-mod-head">
      <span className="kvl-price-tag" data-testid={`tilbod-price-mod-tag-${m.key}`}>{m.tag}</span>
      <h3 className="kvl-price-mod-name">{m.name}</h3>
      {m.who && <p className="kvl-price-mod-who">{m.who}</p>}
    </header>
    <dl className="kvl-price-figs">
      <div className="kvl-price-fig">
        <dt className="kvl-price-label">{pr.figLabels.setup}</dt>
        <dd className="kvl-price-value">
          <span data-num="" data-testid={`tilbod-price-mod-offer-${m.key}`}>{nok(m.offer)}</span>
        </dd>
      </div>
      <div className="kvl-price-fig">
        <dt className="kvl-price-label">{pr.figLabels.licence}</dt>
        <dd className="kvl-price-value">
          <span data-num="" data-testid={`tilbod-price-mod-monthly-${m.key}`}>{nok(m.monthly)}</span>
          <span className="kvl-price-per">{pr.perMonth}</span>
        </dd>
      </div>
    </dl>
    <p className="kvl-price-sub">{m.sub}</p>
    {m.items && (
      <ul className="kvl-price-mod-list" data-testid={`tilbod-price-mod-items-${m.key}`}>
        {m.items.map((x) => (
          <li key={x}>
            <Tick />
            <span>{x}</span>
          </li>
        ))}
      </ul>
    )}
    {m.basis && <p className="kvl-price-note kvl-price-mod-basis">{m.basis}</p>}
  </article>
);

/* the estimate as a table: a row per delivery and one for both. On narrow screens each row folds into a small list. */
const Estimate = ({ est, pr }) => (
  <div className="kvl-price-est" data-testid="tilbod-price-estimate">
    <div className="kvl-price-est-head">
      <span className="kvl-price-label">{est.title}</span>
      <p className="kvl-price-note">{est.sub}</p>
    </div>
    <table className="kvl-price-table">
      <thead>
        <tr>
          {est.head.map((h, i) => (
            <th key={h} scope="col" className={i === 0 ? "is-name" : ""}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {est.rows.map((r) => (
          <tr key={r.key} className={r.total ? "is-total" : ""} data-testid={`tilbod-price-est-${r.key}`}>
            <th scope="row" className="is-name">
              {r.name}
              {r.tag && <small>{r.tag}</small>}
            </th>
            <td data-k={est.head[1]}>{nok(r.offer)}</td>
            <td data-k={est.head[2]}>
              <span>
                {nok(r.monthly)}
                <span className="kvl-price-table-per">{pr.perMonth.replace("/ ", "/")}</span>
              </span>
            </td>
            <td data-k={est.head[3]} className="is-strong" data-testid={`tilbod-price-est-${r.key}-first`}>
              {nok(r.firstYear)}
            </td>
            <td data-k={est.head[4]}>{nok(r.yearAfter)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Pricing = ({ d }) => {
  const pr = d.price;
  const P = pr.model;
  const [ref, seen] = useSeen();
  useCountUp(ref, seen, { delay: 120, duration: 1300, stagger: 120 });
  const [open, setOpen] = useState(false);
  return (
    <div className="kvl-wide kvl-price" data-testid="tilbod-pricing">
      <div ref={ref} className={`kvl-price-sheet ${seen ? "is-in" : ""}`} data-testid="tilbod-price-total">
        <div className="kvl-price-mods" data-testid="tilbod-price-modules">
          {pr.modules.map((m, i) => (
            <PriceModule key={m.key} m={m} pr={pr} i={i} />
          ))}
        </div>

        {pr.estimate && <Estimate est={pr.estimate} pr={pr} />}

        {pr.licence?.covers && (
          <div className="kvl-price-covers" data-testid="tilbod-price-covers">
            <div className="kvl-price-covers-head">
              <span className="kvl-price-label">{pr.licence.covers.title}</span>
              <p className="kvl-price-note">{pr.licence.covers.sub}</p>
            </div>
            <div className="kvl-price-covers-cols">
              <div data-testid="tilbod-price-covers-in">
                <h4 className="kvl-price-covers-h">{pr.licence.covers.inTitle}</h4>
                <ul className="kvl-price-mod-list">
                  {pr.licence.covers.in.map((x) => (
                    <li key={x}>
                      <Tick />
                      <span>{x}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div data-testid="tilbod-price-covers-out">
                <h4 className="kvl-price-covers-h">{pr.licence.covers.outTitle}</h4>
                <ul className="kvl-price-mod-list is-out">
                  {pr.licence.covers.out.map((x) => (
                    <li key={x}>
                      <span className="kvl-price-dash" aria-hidden="true" />
                      <span>{x}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {pr.advice && (
          <div className="kvl-price-advice" data-testid="tilbod-price-advice">
            <span className="kvl-price-label">{pr.advice.title}</span>
            <div className="kvl-price-advice-p">
              <Paras items={pr.advice.p} className="kvl-price-sub" />
            </div>
          </div>
        )}

        <div className="kvl-price-plan" data-testid="tilbod-price-plan">
          <div className="kvl-price-plan-head">
            <span className="kvl-price-label">{pr.planTitle}</span>
            {pr.planSub && <p className="kvl-price-note">{pr.planSub}</p>}
          </div>
          <ol className="kvl-price-plan-steps">
            {P.payments.map((v, i) => (
              <li key={pr.planSteps[i]} data-testid={`tilbod-price-plan-${i + 1}`}>
                <span className="kvl-price-plan-k">{pr.planSteps[i]}</span>
                <span className="kvl-price-plan-v">{nok(v)}</span>
                <span className="kvl-price-plan-pct">{P.split[i]} %</span>
              </li>
            ))}
          </ol>
          {pr.licenceTerms && (
            <p className="kvl-price-note kvl-price-plan-terms" data-testid="tilbod-price-licence-terms">
              {pr.licenceTerms}{" "}
              {pr.licence?.noteLink && (
                <a href={pr.licence.noteLink.href} className="kvl-price-note-link" data-testid="tilbod-price-hosting-link">
                  {pr.licence.noteLink.label} <span aria-hidden="true">→</span>
                </a>
              )}
            </p>
          )}
        </div>
      </div>

      <p className="kvl-price-foot" data-testid="tilbod-price-vat">
        {[pr.vat, pr.notice].filter(Boolean).join(" · ")}
      </p>
      <div className="kvl-price-links">
        <button type="button" className="kvl-price-link" aria-expanded={open} aria-controls="kvl-price-included" onClick={() => setOpen((o) => !o)} data-testid="tilbod-price-included-toggle">
          {pr.links.included} <span aria-hidden="true">{open ? "↑" : "↓"}</span>
        </button>
        <a href="#terms" className="kvl-price-link" data-testid="tilbod-price-total-pay">
          {pr.links.terms} <span aria-hidden="true">→</span>
        </a>
        <a href={`mailto:${d.from.email}`} className="kvl-price-link is-quiet" data-testid="tilbod-price-contact">
          {pr.links.contact}
        </a>
      </div>
      <div id="kvl-price-included" className="kvl-price-included" hidden={!open} data-testid="tilbod-price-included">
        <dl>
          {pr.included.map(([k, v]) => (
            <div key={k}>
              <dt>
                <Tick />
                {k}
              </dt>
              <dd>{v}</dd>
            </div>
          ))}
        </dl>
        <h3 className="kvl-price-scope-h">{d.delivery.includedTitle}</h3>
        <dl className="kvl-price-scope" data-testid="tilbod-included">
          {d.delivery.includedItems.map(([k, v]) => (
            <div key={k}>
              <dt>{k}</dt>
              <dd>{v}</dd>
            </div>
          ))}
        </dl>
        {d.delivery.driftItems && (
          <>
            <h3 className="kvl-price-scope-h">{d.delivery.driftTitle}</h3>
            <dl className="kvl-price-scope" data-testid="tilbod-included-drift">
              {d.delivery.driftItems.map(([k, v]) => (
                <div key={k}>
                  <dt>{k}</dt>
                  <dd>{v}</dd>
                </div>
              ))}
            </dl>
          </>
        )}
        <p className="kvl-price-note kvl-price-outside">{d.delivery.outside}</p>
      </div>
    </div>
  );
};

/* terms: a calm list – the term on the left, one short paragraph on the right, a hairline between each */
const Terms = ({ d, valid }) => {
  const tm = d.terms;
  const when = fmtDate(valid, d.lang);
  return (
    <div className="kvl-wide kvl-terms-wrap" data-testid="tilbod-terms-body">
      <dl className="kvl-terms-list" data-testid="tilbod-terms">
        {tm.items.map(([k, v], i) => (
          <div key={k} data-testid={`tilbod-term-${i + 1}`}>
            <dt>{k}</dt>
            <dd>{v.replace("{validUntil}", when)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

/* who we are: eyebrow and title, the text in two columns, the four of us across the page, three facts and the references */
const About = ({ d }) => {
  const ab = d.about;
  const ref = useReveal();
  const half = Math.ceil(ab.p.length / 2);
  return (
    <section id="about" className="kvl-sec kvl-tone-paper kvl-about-sec" data-head={ab.eyebrow || ab.title.split("\n")[0]} data-tone="paper" data-testid="tilbod-about" ref={ref}>
      <div className="kvl-col kvl-reveal">
        {ab.eyebrow && <p className="kvl-sec-eyebrow">{ab.eyebrow}</p>}
        <Lines text={ab.title} className="kvl-h2" testid="tilbod-about-title" />
      </div>
      <div className="kvl-wide kvl-about-text kvl-reveal kvl-reveal-2" data-testid="tilbod-about-text">
        <div>
          <Paras items={ab.p.slice(0, half)} />
        </div>
        <div>
          <Paras items={ab.p.slice(half)} />
        </div>
      </div>
      <ul className="kvl-wide kvl-team kvl-team-4 kvl-reveal kvl-reveal-2" data-testid="tilbod-team">
        {ab.team.map((m, i) => (
          <li key={m.name} className={m.tag ? "is-lead" : ""} style={{ "--i": i }} data-testid={`tilbod-team-${i + 1}`}>
            <div className="kvl-team-pic">
              <img src={m.img} alt="" width="600" height="660" loading="lazy" decoding="async" />
              {m.tag && <span className="kvl-team-badge">{m.tag}</span>}
            </div>
            <strong>{m.name}</strong>
            <span>{m.role}</span>
            {m.email && (
              <a href={`mailto:${m.email}`} className="kvl-team-mail" data-testid={`tilbod-team-mail-${i + 1}`}>
                {m.email}
              </a>
            )}
          </li>
        ))}
      </ul>
      <div className="kvl-wide kvl-about2-foot kvl-reveal kvl-reveal-2">
        {ab.checks && (
          <ul className="kvl-facts" data-testid="tilbod-about-checks">
            {ab.checks.map((x) => (
              <li key={x}>
                <Check aria-hidden="true" /> {x}
              </li>
            ))}
          </ul>
        )}
        <p className="kvl-p kvl-refs" data-testid="tilbod-refs">
          <span className="kvl-mono">{ab.refsLabel}</span>
          {ab.refItems.map((c, i) => (
            <a key={c.id} href={c.url} target="_blank" rel="noreferrer" data-testid={`tilbod-ref-link-${i + 1}`}>
              {c.k} <span aria-hidden="true">↗</span>
            </a>
          ))}
          {ab.site && (
            <a href={ab.site.url} target="_blank" rel="noreferrer" className="kvl-refs-site" data-testid="tilbod-about-site">
              {ab.site.label} <span aria-hidden="true">↗</span>
            </a>
          )}
        </p>
      </div>
    </section>
  );
};

const Next = ({ d, valid }) => {
  const nx = d.next;
  return (
    <>
      <Paras items={nx.p} />
      <div className="kvl-closing" data-testid="tilbod-next-mail">
        {d.from.photo && <img src={d.from.photo} alt="" className="kvl-closing-photo" loading="lazy" />}
        <div className="kvl-closing-who">
          <span className="kvl-closing-name">{d.from.person}</span>
          <span className="kvl-closing-role">
            {d.from.role} · {d.from.company}
          </span>
          <a href={`mailto:${d.from.email}`}>{d.from.email}</a>
        </div>
        <div className="kvl-closing-meta">
          {nx.confidential && (
            <span className="kvl-closing-conf" data-testid="tilbod-next-confidential">
              {nx.confidential}
            </span>
          )}
          <span className="kvl-mono kvl-meta" data-testid="tilbod-meta">
            {d.ref} · {d.ui.validUntil} {fmtDate(valid, d.lang)}
          </span>
          <span className="kvl-mono kvl-meta">
            {d.from.company} · {d.from.address}
          </span>
        </div>
      </div>
    </>
  );
};

/* the plan: the six weeks as a strip (phases drawn as bars where they happen), then one row per phase –
   when · what we do · what you bring and what comes out of it. The follow-up at the end, dashed. */
const Roadmap = ({ dl }) => {
  const weeks = Array.from({ length: dl.weeks }, (_, i) => i + 1);
  return (
    <div className="kvl-wide kvl-plan" style={{ "--weeks": dl.weeks }} data-testid="tilbod-plan">
      <div className="kvl-plan-strip" aria-hidden="true" data-testid="tilbod-plan-strip">
        <ol className="kvl-plan-weeks">
          {weeks.map((w) => (
            <li key={w}>
              {dl.weekWord} {w}
            </li>
          ))}
          <li className="kvl-plan-weeks-tail">{dl.tail.k}</li>
        </ol>
        <ol className="kvl-plan-bars">
          {dl.phases.map((ph, i) => (
            <li key={ph.name} style={{ gridColumn: `${ph.from} / span ${ph.to - ph.from + 1}`, "--i": i }}>
              <span>{ph.name}</span>
            </li>
          ))}
          <li className="kvl-plan-bar-tail" style={{ gridColumn: `${dl.weeks + 1} / span 1`, "--i": dl.phases.length }}>
            <span>{dl.tail.short}</span>
          </li>
        </ol>
      </div>
      <ol className="kvl-plan-rows">
        {dl.phases.map((ph, i) => (
          <li key={ph.name} style={{ "--i": i }} data-testid={`tilbod-plan-${i + 1}`}>
            <div className="kvl-plan-when">
              <span className="kvl-plan-week">{ph.week}</span>
              <h3 className="kvl-plan-name">{ph.name}</h3>
            </div>
            <p className="kvl-plan-p">{ph.p}</p>
            {ph.input && (
              <dl className="kvl-plan-io" data-testid={`tilbod-plan-io-${i + 1}`}>
                <div>
                  <dt>{dl.labels.input}</dt>
                  <dd>{ph.input}</dd>
                </div>
                <div>
                  <dt>{dl.labels.result}</dt>
                  <dd>{ph.result}</dd>
                </div>
              </dl>
            )}
          </li>
        ))}
        <li className="kvl-plan-tail" style={{ "--i": dl.phases.length }} data-testid="tilbod-plan-after">
          <div className="kvl-plan-when">
            <span className="kvl-plan-week">{dl.tail.k}</span>
            <h3 className="kvl-plan-name">{dl.tail.name}</h3>
          </div>
          <p className="kvl-plan-p">{dl.after}</p>
        </li>
      </ol>
      {dl.note && (
        <p className="kvl-small kvl-plan-note" data-testid="tilbod-plan-note">
          {dl.note}
        </p>
      )}
    </div>
  );
};

export const ProductProposal = ({ d }) => {
  useKeys(d.pages);
  useMeta(`${d.ui.proposal} · ${d.to.company} – ${d.from.company}`);
  const wide = useWide();
  const [liveOpen, setLiveOpen] = useState(false);
  const date = d.issued ? new Date(`${d.issued}T12:00:00`) : new Date();
  const valid = new Date(date.getTime() + d.validDays * 86400000);
  const { idea, flow, visma, delivery: dl, price: pr } = d;

  return (
    <div className="kvp kvl" lang={d.lang} data-testid="tilbod-page">
      <a className="kvp-skip" href="#letter">
        {d.ui.skip}
      </a>
      <Top d={d} />
      <main>
        <Hero d={d} date={date} valid={valid} />
        <Letter d={d} />
        <Demo d={d} />
        <LiveSystem d={d} open={liveOpen} onOpenChange={setLiveOpen} compact={!wide} />

        <Sec
          id="idea"
          eyebrow={idea.eyebrow}
          title={idea.title}
          testid="tilbod-idea"
          className="kvl-sec-center"
          figure={
            <>
              <ul className="kvl-wide kvl-idea-points" data-testid="tilbod-idea-points">
                {idea.points.map(([k, v], i) => (
                  <li key={k} style={{ "--i": i }} data-testid={`tilbod-idea-point-${i + 1}`}>
                    <span className="kvl-idea-n" aria-hidden="true">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h3 className="kvl-idea-k">{k}</h3>
                    <p className="kvl-idea-v">{v}</p>
                  </li>
                ))}
              </ul>
              <SystemShots d={d} />
            </>
          }
        >
          <p className="kvl-p kvl-idea-lede" data-testid="tilbod-idea-lede">
            {idea.lede}
          </p>
        </Sec>

        <Sec id="flow" eyebrow={flow.eyebrow} title={flow.title} head={flow.head} testid="tilbod-flow" tone="white" className="kvl-flow-sec" figure={<Flow f={flow} />}>
          {flow.lede && (
            <p className="kvl-p" data-testid="tilbod-flow-lede">
              {flow.lede}
            </p>
          )}
        </Sec>

        <AiSection d={d} />

        <Sec id="visma" eyebrow={visma.eyebrow} title={visma.title} testid="tilbod-visma" tone="white" className="kvl-visma-sec" figure={<Exchange v={visma} />}>
          <Paras items={visma.p} />
        </Sec>

        {d.platform && (
          <Sec id="platform" eyebrow={d.platform.eyebrow} title={d.platform.title} testid="tilbod-platform" tone="sand" figure={<Platform pf={d.platform} />}>
            <div data-testid="tilbod-platform-lede">
              <Paras items={d.platform.lede} />
            </div>
          </Sec>
        )}

        <Sec id="delivery" eyebrow={dl.eyebrow} title={dl.title} testid="tilbod-delivery" figure={<Roadmap dl={dl} />}>
          <div data-testid="tilbod-delivery-lede">
            <Paras items={dl.p} />
          </div>
        </Sec>

        <Sec id="price" eyebrow={pr.eyebrow} title={pr.title} testid="tilbod-price" tone="sand" className="kvl-price-sec" figure={<Pricing d={d} />}>
          <p className="kvl-p kvl-price-lead" data-testid="tilbod-price-lead">
            {pr.sub}
          </p>
        </Sec>

        {d.terms && (
          <Sec id="terms" title={d.terms.title} testid="tilbod-terms-sec" tone="white" figure={<Terms d={d} valid={valid} />}>
            <p className="kvl-p">{d.terms.p}</p>
          </Sec>
        )}

        {d.about && <About d={d} />}

        <Sec id="next" title={d.next.title} testid="tilbod-next" tone="white" className="kvl-sec-tight">
          <Next d={d} valid={valid} />
        </Sec>

        <footer className="kvl-foot kvl-tone-white" data-testid="tilbod-footer">
          <div className="kvl-wide kvl-foot-in">
            <div className="kvl-foot-brand">
              <a href="https://kviteberg.no" target="_blank" rel="noreferrer" className="kvl-foot-logo" aria-label="Kviteberg" data-testid="tilbod-footer-logo">
                <KvitebergLogo className="kvl-logo-svg" />
              </a>
              <span>
                {d.from.company} · {d.from.address}
              </span>
            </div>
            <span>
              {d.ref} · {d.ui.confidential}
            </span>
          </div>
        </footer>
      </main>
    </div>
  );
};
